import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Search, ChevronLeft, ChevronRight, Plane, LayoutGrid, Eye,
  Filter, ArrowUp, ArrowDown, ChevronDown, StickyNote
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useSubscriptionGuard } from '@/hooks/useSubscriptionGuard';
import { Loader2 } from 'lucide-react';
import { fsopApi, type ViewConfig, type FlightLog } from '@/services/api';
import { FlightLogNotesModal } from '@/components/fsop/FlightLogNotesModal';
import { SubscriptionDialog } from '@/components/ui/subscription-dialog';

// camelCase keys — 与 transformResponse 后的 FlightLog 字段名一致
const FIELD_LABELS: Record<string, string> = {
  airDate:'飞行日期',airNum:'航班号',airModel:'机型',airline:'航线',
  departure:'出发地',arrival:'到达地',flightTime:'飞行时间',
  nightTime:'夜间时间',dayTime:'昼间时间',recordType:'记录类型',state:'状态',
  captain:'机长',copilot:'副驾驶',aircraftReg:'航空器注册号',
  takeoffAirport:'起飞机场',landingAirport:'降落机场',
  landingCount:'起落次数',dayLanding:'昼间起落',nightLanding:'夜间起落',
  simulatorNum:'模拟机编号',trainingType:'训练类型',
};

function formatFlightTime(minutes:number|null|undefined):string {
  if (!minutes && minutes !== 0) return '-';
  return `${Math.floor(minutes/60)}h${minutes%60}m`;
}

interface FilterState {
  recordType: string; airNum: string; airModel: string;
  airline: string; startDate: string; endDate: string;
}

const DEFAULT_FILTER:FilterState = {recordType:'',airNum:'',airModel:'',airline:'',startDate:'',endDate:''};

export default function FsopFlightLogsPage() {
  const {token} = useAuthStore();
  const navigate = useNavigate();

  const [views, setViews] = useState<ViewConfig[]>([]);
  const [activeView, setActiveView] = useState<ViewConfig|null>(null);
  const [logs, setLogs] = useState<FlightLog[]>([]);
  const [pagination, setPagination] = useState<{count:number;next:string|null;previous:string|null}|null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sort, setSort] = useState({sortBy:'airDate',order:'desc' as 'asc'|'desc'});
  const [filter, setFilter] = useState<FilterState>(DEFAULT_FILTER);
  const [filterOptions, setFilterOptions] = useState({recordTypes:[] as string[],airNums:[] as string[],airModels:[] as string[],airlines:[] as string[]});
  const [filterOpen, setFilterOpen] = useState(false);
  const [manualFilter, setManualFilter] = useState<Partial<FilterState>>({});
  const [activeNoteFlightLogId, setActiveNoteFlightLogId] = useState<number | null>(null);
  const { showDialog, setShowDialog } = useSubscriptionGuard();

  const fetchViews = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fsopApi.getViewConfigs({ pageSize: 100 });
      // 后端返回标准分页格式: { count, results, next, previous }
      const list = (res.data?.results ?? []) as ViewConfig[];
      setViews(list);
      const def = list.find((v: ViewConfig) => v.isDefault) || list[0];
      if (def) {
        setActiveView(def);
        // 后端已返回 camelCase 格式的 filters
        const f = def.filters || {};
        setFilter({
          recordType: f.recordType || '',
          airNum: f.airNum || '',
          airModel: f.airModel || '',
          airline: f.airline || '',
          startDate: f.startDate || '',
          endDate: f.endDate || '',
        });
        setSort({ sortBy: def.sortBy, order: def.sortOrder as 'asc' | 'desc' });
      }
    } catch {
      // 静默处理错误
    }
  }, [token]);

  const fetchFilterOptions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fsopApi.getFilterOptions();
      // 后端返回 camelCase 格式: { success, data: { recordTypes, airNums, airModels, airlines } }
      const d = res.data?.data ?? {};
      setFilterOptions({
        recordTypes: d.recordTypes ?? [],
        airNums: d.airNums ?? [],
        airModels: d.airModels ?? [],
        airlines: d.airlines ?? [],
      });
    } catch {
      // 静默处理错误
    }
  }, [token]);

  const fetchLogs = useCallback(async (page = 1) => {
    if (!token || !activeView) return;
    setLoading(true);
    try {
      const f = { ...filter, ...manualFilter };
      const r = await fsopApi.getFlightLogsWithConfig(activeView.id, {
        page,
        pageSize: activeView.pageSize,
        sortBy: sort.sortBy,
        order: sort.order,
        search: searchKeyword || undefined,
        recordType: f.recordType || undefined,
        airNum: f.airNum || undefined,
        airModel: f.airModel || undefined,
        airline: f.airline || undefined,
        startDate: f.startDate || undefined,
        endDate: f.endDate || undefined,
      });
      // 后端返回 DRF 标准分页格式: { count, next, previous, results, config }
      const d = r.data;
      setLogs((d?.results ?? []) as FlightLog[]);
      setPagination({ count: d?.count ?? 0, next: d?.next ?? null, previous: d?.previous ?? null });
      setCurrentPage(page);
    } catch (err: any) {
      console.error('Failed to fetch logs', err);
      if (err.response?.status === 402) {
        setShowDialog(true);
      }
    } finally {
      setLoading(false);
    }
  }, [token, activeView, sort, searchKeyword, filter, manualFilter]);

  useEffect(()=>{ fetchViews(); fetchFilterOptions(); },[fetchViews,fetchFilterOptions]);

  // 当排序、筛选、视图变化时自动重新获取数据（搜索关键词在表单提交时单独触发，避免输入过程中频繁请求）
  useEffect(()=>{
    if(activeView) fetchLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[activeView, sort.sortBy, sort.order, filter, fetchLogs]);

  const handleViewChange = (viewId: number) => {
    const view = views.find(v => v.id === viewId);
    if (!view) return;
    setActiveView(view);
    // 直接使用 camelCase 格式的 filters
    const f = view.filters || {};
    setFilter({
      recordType: f.recordType || '',
      airNum: f.airNum || '',
      airModel: f.airModel || '',
      airline: f.airline || '',
      startDate: f.startDate || '',
      endDate: f.endDate || '',
    });
    setManualFilter({});
    setSort({ sortBy: view.sortBy, order: view.sortOrder as 'asc' | 'desc' });
    setCurrentPage(1);
  };

  const handleSort = (field:string) => {
    setSort(prev=>({sortBy:field,order:prev.sortBy===field&&prev.order==='desc'?'asc':'desc'}));
    setCurrentPage(1);
  };

  const applyFilter = () => {
    setFilter(prev=>({...prev,...manualFilter}));
    setManualFilter({}); setFilterOpen(false); setCurrentPage(1);
  };

  const clearFilter = () => {
    const f = activeView?.filters || {};
    setFilter({
      recordType: f.recordType || '',
      airNum: f.airNum || '',
      airModel: f.airModel || '',
      airline: f.airline || '',
      startDate: f.startDate || '',
      endDate: f.endDate || '',
    });
    setManualFilter({});
    setCurrentPage(1);
  };

  const activeFilterCount = [filter.recordType, filter.airNum, filter.airModel, filter.airline, filter.startDate, filter.endDate].filter(Boolean).length;

  // 直接使用 camelCase 格式的 selectedFields
  const selectedFields = activeView?.selectedFields?.length ? activeView.selectedFields : Object.keys(FIELD_LABELS);
  const totalPages = pagination ? Math.ceil(pagination.count / (activeView?.pageSize || 20)) : 0;

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
            <Plane className="w-6 h-6 text-white" />
          </div>
          <div><h1 className="text-2xl font-bold text-gray-900">飞行记录</h1>
            <p className="text-sm text-gray-500">查看和筛选您的飞行日志</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={()=>navigate('/fsop/flightlogs/views')} className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <LayoutGrid className="w-4 h-4" />视图配置
          </button>
          <button onClick={()=>navigate('/fsop/flightlogs/sync')} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">同步数据</button>
        </div>
      </div>

      {/* 工具栏 */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-shrink-0">
              <Label className="text-xs text-gray-500 mb-1 block"><Eye className="w-3 h-3 inline mr-1"/>当前视图</Label>
              {views.length===0 ? (
                <div className="text-sm text-gray-400 py-2">暂无视图配置<button onClick={()=>navigate('/fsop/flightlogs/views')} className="ml-2 text-blue-600 hover:underline">去创建 →</button></div>
              ) : (
                <select className="border rounded-lg px-3 py-2 text-sm min-w-[160px]" value={activeView?.id||''} onChange={e=>handleViewChange(Number(e.target.value))}>
                  {views.map(v=><option key={v.id} value={v.id}>{v.isDefault?'⭐ ':''}{v.name}</option>)}
                </select>
              )}
            </div>

            <form onSubmit={e=>{e.preventDefault();setCurrentPage(1);fetchLogs(1);}} className="flex-1 flex gap-2 min-w-[200px]">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input className="pl-9" placeholder="搜索航班号、航线..." value={searchKeyword}
                  onChange={e=>setSearchKeyword(e.target.value)} />
              </div>
              <Button type="submit" variant="outline" size="sm">搜索</Button>
              {searchKeyword&&<Button type="button" variant="ghost" size="sm" onClick={()=>{setSearchKeyword('');setCurrentPage(1);fetchLogs(1);}}>清除</Button>}
            </form>

            <div className="relative">
              <Button variant={filterOpen||activeFilterCount>0?'default':'outline'} size="sm"
                onClick={()=>setFilterOpen(v=>!v)} className={filterOpen||activeFilterCount>0?'bg-blue-600':''}>
                <Filter className="w-4 h-4 mr-1"/>筛选
                {activeFilterCount>0&&<span className="ml-1.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{activeFilterCount}</span>}
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${filterOpen?'rotate-180':''}`} />
              </Button>

              {filterOpen&&(
                <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm">筛选条件</span>
                    <button onClick={clearFilter} className="text-xs text-red-500 hover:underline">清除全部</button>
                  </div>
                  <div className="space-y-3">
                    {([
                      ['recordType','记录类型',filterOptions.recordTypes],
                      ['airNum','航班号',filterOptions.airNums],
                      ['airModel','机型',filterOptions.airModels],
                      ['airline','航线',filterOptions.airlines],
                    ] as [string, string, string[]][]).map(([k,l,o])=>(
                      <div key={k}>
                        <Label className="text-xs text-gray-500 mb-1 block">{l}</Label>
                        <select className="w-full border rounded-lg px-2 py-1.5 text-sm"
                          value={(manualFilter as any)[k]||''} onChange={e=>setManualFilter(f=>({...f,[k]:e.target.value}))}>
                          <option value="">全部</option>
                          {(o as string[]).map(v=><option key={v} value={v}>{v}</option>)}
                        </select>
                      </div>
                    ))}
                    <div className="grid grid-cols-2 gap-2">
                      {[['startDate','开始日期'],['endDate','结束日期']].map(([k,l])=>(
                        <div key={k}>
                          <Label className="text-xs text-gray-500 mb-1 block">{l}</Label>
                          <Input type="date" className="text-sm" value={(manualFilter as any)[k]||''}
                            onChange={e=>setManualFilter(f=>({...f,[k]:e.target.value}))} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={applyFilter}>应用筛选</Button>
                    <Button size="sm" variant="outline" onClick={()=>{setFilterOpen(false);setManualFilter({});}}>取消</Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {activeFilterCount>0&&(
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
              {filter.recordType&&<span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"><Filter className="w-3 h-3"/>记录类型: {filter.recordType}</span>}
              {filter.airNum&&<span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"><Filter className="w-3 h-3"/>航班号: {filter.airNum}</span>}
              {filter.airModel&&<span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"><Filter className="w-3 h-3"/>机型: {filter.airModel}</span>}
              {filter.airline&&<span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"><Filter className="w-3 h-3"/>航线: {filter.airline}</span>}
              {(filter.startDate||filter.endDate)&&<span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"><Filter className="w-3 h-3"/>日期: {filter.startDate||'...'} ~ {filter.endDate||'...'}</span>}
              <button onClick={clearFilter} className="text-xs text-red-500 hover:underline ml-1">清除全部</button>
            </div>
          )}
        </CardContent>
      </Card>

      {pagination&&(
        <div className="text-sm text-gray-500 mb-3 px-1 flex items-center justify-between">
          <div>共 <span className="font-medium text-gray-900">{pagination.count}</span> 条记录{activeView&&<span className="ml-2">· 每页 {activeView.pageSize} 条</span>}</div>
          <div className="text-gray-400">当前排序: <span className="text-gray-600">{FIELD_LABELS[sort.sortBy]||sort.sortBy}</span>{sort.order==='desc'?' ↓ 降序':' ↑ 升序'}</div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex items-center justify-center h-48"><Loader2 className="w-8 h-8 animate-spin text-blue-500"/></div>
           : logs.length===0 ? (
            <div className="text-center py-16">
              <Plane className="w-12 h-12 text-gray-200 mx-auto mb-4"/>
              <p className="text-gray-400 mb-3">暂无飞行记录</p>
              <button onClick={()=>navigate('/fsop/flightlogs/sync')} className="text-sm text-blue-600 hover:underline">立即同步 →</button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    {selectedFields.map(key=>(
                      <th key={key}
                        className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap cursor-pointer select-none hover:text-blue-600 hover:bg-gray-100 transition-colors"
                        onClick={()=>handleSort(key)}>
                        <span className="flex items-center gap-1">
                          {FIELD_LABELS[key]||key}
                          {sort.sortBy===key ? (
                            sort.order==='desc' ? <ArrowDown className="w-3.5 h-3.5 text-blue-500"/>
                             : <ArrowUp className="w-3.5 h-3.5 text-blue-500"/>
                          ) : (
                            <span className="text-gray-300 flex flex-col leading-none"><ArrowUp className="w-2.5 h-2.5 -mb-1"/><ArrowDown className="w-2.5 h-2.5"/></span>
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="text-left px-4 py-3 font-medium text-gray-500 whitespace-nowrap sticky right-0 bg-gray-50">
                      笔记
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {logs.map(log=>(
                    <tr key={log.id} className="hover:bg-blue-50/50 transition-colors">
                      {selectedFields.map(key=>(
                        <td key={key} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {key==='flightTime'||key==='nightTime'||key==='dayTime'
                            ? formatFlightTime(log[key])
                            : log[key]!==null&&log[key]!==undefined ? String(log[key]) : '-'}
                        </td>
                      ))}
                      <td className="px-4 py-3 whitespace-nowrap sticky right-0 bg-white">
                        <button
                          onClick={() => setActiveNoteFlightLogId(log.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                          title="查看笔记"
                        >
                          <StickyNote className="w-3.5 h-3.5" />
                          笔记
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {activeNoteFlightLogId && (
        <FlightLogNotesModal
          flightLogId={activeNoteFlightLogId}
          onClose={() => setActiveNoteFlightLogId(null)}
        />
      )}

      {pagination&&pagination.count>0&&(
          <div className="flex items-center justify-between px-6 py-4 border-t">
            <div className="text-sm text-gray-500">第 {currentPage} / {totalPages} 页，共 {pagination.count} 条</div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={()=>fetchLogs(currentPage-1)} disabled={!pagination.previous||loading}>
                <ChevronLeft className="w-4 h-4 mr-1"/>上一页
              </Button>
              <Button variant="outline" size="sm" onClick={()=>fetchLogs(currentPage+1)} disabled={!pagination.next||loading}>
                下一页<ChevronRight className="w-4 h-4 ml-1"/>
              </Button>
            </div>
          </div>
        )}
      </Card>

      <SubscriptionDialog
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
      />
    </div>
  );
}
